import { createSlice } from "@reduxjs/toolkit";

import {
  IAddCashResponse,
  ICashierDetailsResponse,
  ICashierPointResponse,
  ICategorySummary,
  ICloseCashierResponse,
  IDayEndStationSummaryResponse,
  IEndDayResponse,
  ILatestCashierSessionResponse,
  IOpenCashierResponse,
  IRefundResponse,
  IRemoveCashResponse,
  IStationCashierSummaryResponse,
  IStationSummaryResponse,
  IStoreSummaryResponse,
  storeStatus,
} from "../../typings/interfaces/responses/cashierStationResponses";
import { StationManagementDto } from "../../typings/interfaces/responses/stationManagementResponses";
import {
  addCashAction,
  closeStationCashierAction,
  denyStationAccessAction,
  endDayAction,
  getAllCashierPointsActions,
  getAllDayEndStationSummaryAction,
  getAllStationsAction,
  getAllStationSummaryAction,
  getAllStationsWithStatusAction,
  getAvailableStationsForOnboardingAction,
  deactivateStationAction,
  activateStationAction,
  getCashierDetailsAction,
  getCashierLatestSessionAction,
  getRefundsForStationAction,
  getRefundsForStoreAction,
  getStationByIdAction,
  getStationCashierSummaryAction,
  getStationSummaryAction,
  getStoreCategorySummaryAction,
  getStoreStatusAction,
  getStoreSummaryAction,
  openStationCashierAction,
  removeCashAction,
  requestNewStationAction,
  updateStationAccessAction,
  getStationAccessSessionsAction,
  adminCloseStationAction,
} from "../actions/cashierStationActions";

export interface ICashierStationState {
  activeStation: ICashierPointResponse | null;
  activeStationCashier:
    | (IOpenCashierResponse & { status?: "open" | "closed" | null })
    | null;
  // Flips true once useAppInitializer has attempted to rehydrate
  // activeStationCashier from localStorage (regardless of whether a session
  // was found). Lets route guards hold off rendering station-dependent UI
  // (e.g. Lobby's action buttons) until that check has actually run, instead
  // of briefly showing a disabled/unknown state on every fresh page load.
  stationCashierHydrated: boolean;

  requestNewCahierResponse: ICashierPointResponse | null;
  requestNewCahierLoading: boolean;
  requestNewCahierError: string | null;

  getStoreCategorySummaryResponse: ICategorySummary[] | null;
  getStoreCategorySummaryLoading: boolean;
  getStoreCategorySummaryError: string | null;

  getStoreStatusResponse: storeStatus | null;
  getStoreStatusLoading: boolean;
  getStoreStatusError: string | null;

  cashierDetails: ICashierDetailsResponse | null;
  cashierDetailsLoading: boolean;
  cashierDetailsError: string | null;

  refundsForStation: IRefundResponse[] | null;
  refundsForStationLoading: boolean;
  refundsForStationError: string | null;

  refundsForStore: IRefundResponse[] | null;
  refundsForStoreLoading: boolean;
  refundsForStoreError: string | null;

  allCashierStationRequests: ICashierPointResponse[] | null;
  getAllCashierStationRequestsLoading: boolean;
  getAllCashierStationRequestsError: string | null;

  approvedCashierStationRequests: ICashierPointResponse[] | null;
  getApprovedCashierStationRequestsLoading: boolean;
  getApprovedCashierStationRequestsError: string | null;

  rejectedCashierStationRequests: ICashierPointResponse[] | null;
  getRejectedCashierStationRequestsLoading: boolean;
  getRejectedCashierStationRequestsError: string | null;

  getCashierStationByIdResponse: ICashierPointResponse | null;
  getCashierStationByIdLoading: boolean;
  getCashierStationByIdError: string | null;

  updateCashierStationAccessResponse: ICashierPointResponse | null;
  updateCashierStationAccessLoading: boolean;
  updateCashierStationAccessError: string | null;

  denyCashierStationAccessResponse: ICashierPointResponse | null;
  denyCashierStationAccessLoading: boolean;
  denyCashierStationAccessError: string | null;

  openStationCashierResponse: IOpenCashierResponse | null;
  openStationCashierLoading: boolean;
  openStationCashierError: string | null;

  getStationCashierSummaryResponse: IStationCashierSummaryResponse | null;
  getStationCashierSummaryLoading: boolean;
  getStationCashierSummaryError: string | null;

  getStationSummaryResponse: IStationSummaryResponse | null;
  getStationSummaryLoading: boolean;
  getStationSummaryError: string | null;

  getStoreSummaryResponse: IStoreSummaryResponse | null;
  getStoreSummaryLoading: boolean;
  getStoreSummaryError: string | null;

  addCashResponse: IAddCashResponse[] | null;
  addCashLoading: boolean;
  addCashError: string | null;

  removeCashResponse: IRemoveCashResponse[] | null;
  removeCashLoading: boolean;
  removeCashError: string | null;

  closeStationCashierResponse: ICloseCashierResponse | null;
  closeStationCashierLoading: boolean;
  closeStationCashierError: string | null;

  cashierPoints: ICashierPointResponse[] | null;
  cashierPointsLoading: boolean;
  cashierPointsError: string | null;

  getAllStationSummaryResponse: IStationSummaryResponse[] | null;
  getAllStationSummaryLoading: boolean;
  getAllStationSummaryError: string | null;

  dayEndStationSummaryResponse: IDayEndStationSummaryResponse[] | null;
  dayEndStationSummaryLoading: boolean;
  dayEndStationSummaryError: string | null;

  isRegisterCleaned: boolean;

  endDayResponse: IEndDayResponse | null;
  endDayLoading: boolean;
  endDayError: string | null;

  cashierLatestSession: ILatestCashierSessionResponse | null;
  cashierLatestSessionLoading: boolean;
  cashierLatestSessionError: string | null;

  // Add new properties for enhanced station management
  allStationsWithStatus: StationManagementDto[] | null;
  getAllStationsWithStatusLoading: boolean;
  getAllStationsWithStatusError: string | null;

  availableStationsForOnboarding: ICashierPointResponse[] | null;
  getAvailableStationsForOnboardingLoading: boolean;
  getAvailableStationsForOnboardingError: string | null;

  stationAccessSessions: any[] | null;
  stationAccessSessionsLoading: boolean;
  stationAccessSessionsError: string | null;

  adminCloseStationResponse: ICloseCashierResponse | null;
  adminCloseStationLoading: boolean;
  adminCloseStationError: string | null;
}

const initialState: ICashierStationState = {
  activeStation: null,
  activeStationCashier: null,
  stationCashierHydrated: false,

  requestNewCahierResponse: null,
  requestNewCahierLoading: false,
  requestNewCahierError: null,

  cashierDetails: null,
  cashierDetailsLoading: false,
  cashierDetailsError: null,

  refundsForStation: null,
  refundsForStationLoading: false,
  refundsForStationError: null,

  refundsForStore: null,
  refundsForStoreLoading: false,
  refundsForStoreError: null,

  getStoreCategorySummaryResponse: null,
  getStoreCategorySummaryLoading: false,
  getStoreCategorySummaryError: null,

  getStoreStatusResponse: null,
  getStoreStatusLoading: false,
  getStoreStatusError: null,

  cashierPoints: null,
  cashierPointsLoading: false,
  cashierPointsError: null,

  allCashierStationRequests: null,
  getAllCashierStationRequestsLoading: false,
  getAllCashierStationRequestsError: null,

  approvedCashierStationRequests: null,
  getApprovedCashierStationRequestsLoading: false,
  getApprovedCashierStationRequestsError: null,

  rejectedCashierStationRequests: null,
  getRejectedCashierStationRequestsLoading: false,
  getRejectedCashierStationRequestsError: null,

  getCashierStationByIdResponse: null,
  getCashierStationByIdLoading: false,
  getCashierStationByIdError: null,

  updateCashierStationAccessResponse: null,
  updateCashierStationAccessLoading: false,
  updateCashierStationAccessError: null,

  denyCashierStationAccessResponse: null,
  denyCashierStationAccessLoading: false,
  denyCashierStationAccessError: null,

  openStationCashierResponse: null,
  openStationCashierLoading: false,
  openStationCashierError: null,

  getStationCashierSummaryResponse: null,
  getStationCashierSummaryLoading: false,
  getStationCashierSummaryError: null,

  getStationSummaryResponse: null,
  getStationSummaryLoading: false,
  getStationSummaryError: null,

  getStoreSummaryResponse: null,
  getStoreSummaryLoading: false,
  getStoreSummaryError: null,

  addCashResponse: null,
  addCashLoading: false,
  addCashError: null,

  removeCashResponse: null,
  removeCashLoading: false,
  removeCashError: null,

  closeStationCashierResponse: null,
  closeStationCashierLoading: false,
  closeStationCashierError: null,

  getAllStationSummaryResponse: null,
  getAllStationSummaryLoading: false,
  getAllStationSummaryError: null,

  dayEndStationSummaryResponse: null,
  dayEndStationSummaryLoading: false,
  dayEndStationSummaryError: null,

  isRegisterCleaned: false,

  endDayResponse: null,
  endDayLoading: false,
  endDayError: null,

  cashierLatestSession: null,
  cashierLatestSessionLoading: false,
  cashierLatestSessionError: null,

  // Add new initial state values for enhanced station management
  allStationsWithStatus: null,
  getAllStationsWithStatusLoading: false,
  getAllStationsWithStatusError: null,

  availableStationsForOnboarding: null,
  getAvailableStationsForOnboardingLoading: false,
  getAvailableStationsForOnboardingError: null,

  stationAccessSessions: null,
  stationAccessSessionsLoading: false,
  stationAccessSessionsError: null,

  adminCloseStationResponse: null,
  adminCloseStationLoading: false,
  adminCloseStationError: null,
};

const cashierStationSlice = createSlice({
  name: "cashierStation",
  initialState,
  reducers: {
    setActiveStation: (state, action) => {
      state.activeStation = action.payload;
    },
    setActiveStationCashier: (state, action) => {
      state.activeStationCashier = action.payload;
    },
    setStationCashierHydrated: (state, action) => {
      state.stationCashierHydrated = action.payload;
    },
    clearOpenStationCashierResponse: (state) => {
      state.openStationCashierResponse = null;
    },
    setIsRegisterCleaned: (state) => {
      state.isRegisterCleaned = true;
    },
    clearAddCashResponse: (state) => {
      state.addCashResponse = null;
    },
    clearRemoveCashResponse: (state) => {
      state.removeCashResponse = null;
    },
    clearDayEndSummaries: (state) => {
      state.dayEndStationSummaryResponse = [];
    },
    clearEndDayResponse: (state) => {
      state.endDayResponse = null;
      state.endDayError = null;
      state.endDayLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(requestNewStationAction.pending, (state) => {
        state.requestNewCahierLoading = true;
        state.requestNewCahierError = null;
        state.requestNewCahierResponse = null;
      })
      .addCase(requestNewStationAction.fulfilled, (state, action) => {
        state.requestNewCahierLoading = false;
        state.requestNewCahierResponse = action.payload;
        state.requestNewCahierError = null;
      })
      .addCase(requestNewStationAction.rejected, (state, action) => {
        state.requestNewCahierLoading = false;
        state.requestNewCahierError = action.payload as string;
        state.requestNewCahierResponse = null;
      });

    builder
      .addCase(getAllStationsAction.pending, (state) => {
        state.getAllCashierStationRequestsLoading = true;
        state.getAllCashierStationRequestsError = null;
        state.allCashierStationRequests = null;
      })
      .addCase(getAllStationsAction.fulfilled, (state, action) => {
        state.getAllCashierStationRequestsLoading = false;
        state.allCashierStationRequests = action.payload;
        state.getAllCashierStationRequestsError = null;
      })
      .addCase(getAllStationsAction.rejected, (state, action) => {
        state.getAllCashierStationRequestsLoading = false;
        state.getAllCashierStationRequestsError = action.payload as string;
        state.allCashierStationRequests = null;
      });

    builder
      .addCase(getStationByIdAction.pending, (state) => {
        state.getCashierStationByIdLoading = true;
        state.getCashierStationByIdError = null;
        state.getCashierStationByIdResponse = null;
      })
      .addCase(getStationByIdAction.fulfilled, (state, action) => {
        state.getCashierStationByIdLoading = false;
        state.getCashierStationByIdResponse = action.payload;
        state.getCashierStationByIdError = null;
      })
      .addCase(getStationByIdAction.rejected, (state, action) => {
        state.getCashierStationByIdLoading = false;
        state.getCashierStationByIdError = action.payload as string;
        state.getCashierStationByIdResponse = null;
      });

    builder
      .addCase(updateStationAccessAction.pending, (state) => {
        state.updateCashierStationAccessLoading = true;
        state.updateCashierStationAccessError = null;
        state.updateCashierStationAccessResponse = null;
      })
      .addCase(updateStationAccessAction.fulfilled, (state, action) => {
        state.updateCashierStationAccessLoading = false;
        state.updateCashierStationAccessResponse = action.payload;
        state.updateCashierStationAccessError = null;
      })
      .addCase(updateStationAccessAction.rejected, (state, action) => {
        state.updateCashierStationAccessLoading = false;
        state.updateCashierStationAccessError = action.payload as string;
        state.updateCashierStationAccessResponse = null;
      });

    builder
      .addCase(denyStationAccessAction.pending, (state) => {
        state.denyCashierStationAccessLoading = true;
        state.denyCashierStationAccessError = null;
        state.denyCashierStationAccessResponse = null;
      })
      .addCase(denyStationAccessAction.fulfilled, (state, action) => {
        state.denyCashierStationAccessLoading = false;
        state.denyCashierStationAccessResponse = action.payload;
        state.denyCashierStationAccessError = null;
      })
      .addCase(denyStationAccessAction.rejected, (state, action) => {
        state.denyCashierStationAccessLoading = false;
        state.denyCashierStationAccessError = action.payload as string;
        state.denyCashierStationAccessResponse = null;
      });

    builder
      .addCase(openStationCashierAction.pending, (state) => {
        state.openStationCashierLoading = true;
        state.openStationCashierError = null;
        state.openStationCashierResponse = null;
      })
      .addCase(openStationCashierAction.fulfilled, (state, action) => {
        state.openStationCashierLoading = false;
        state.openStationCashierResponse = action.payload;
        state.openStationCashierError = null;
      })
      .addCase(openStationCashierAction.rejected, (state, action) => {
        state.openStationCashierLoading = false;
        state.openStationCashierError = action.payload as string;
        state.openStationCashierResponse = null;
      });

    builder
      .addCase(getStationCashierSummaryAction.pending, (state) => {
        state.getStationCashierSummaryLoading = true;
        state.getStationCashierSummaryError = null;
        state.getStationCashierSummaryResponse = null;
      })
      .addCase(getStationCashierSummaryAction.fulfilled, (state, action) => {
        state.getStationCashierSummaryLoading = false;
        state.getStationCashierSummaryResponse = action.payload[0];
        state.getStationCashierSummaryError = null;
      })
      .addCase(getStationCashierSummaryAction.rejected, (state, action) => {
        state.getStationCashierSummaryLoading = false;
        state.getStationCashierSummaryError = action.payload as string;
        state.getStationCashierSummaryResponse = null;
      });

    builder
      .addCase(getStationSummaryAction.pending, (state) => {
        state.getStationSummaryLoading = true;
        state.getStationSummaryError = null;
        state.getStationSummaryResponse = null;
      })
      .addCase(getStationSummaryAction.fulfilled, (state, action) => {
        state.getStationSummaryLoading = false;
        state.getStationSummaryResponse = action.payload;
        state.getStationSummaryError = null;
      })
      .addCase(getStationSummaryAction.rejected, (state, action) => {
        state.getStationSummaryLoading = false;
        state.getStationSummaryError = action.payload as string;
        state.getStationSummaryResponse = null;
      });

    builder
      .addCase(getStoreSummaryAction.pending, (state) => {
        state.getStoreSummaryLoading = true;
        state.getStoreSummaryError = null;
        state.getStoreSummaryResponse = null;
      })
      .addCase(getStoreSummaryAction.fulfilled, (state, action) => {
        state.getStoreSummaryLoading = false;
        state.getStoreSummaryResponse = action.payload;
        state.getStoreSummaryError = null;
      })
      .addCase(getStoreSummaryAction.rejected, (state, action) => {
        state.getStoreSummaryLoading = false;
        state.getStoreSummaryError = action.payload as string;
        state.getStoreSummaryResponse = null;
      });

    builder
      .addCase(addCashAction.pending, (state) => {
        state.addCashLoading = true;
        state.addCashError = null;
        state.addCashResponse = null;
      })
      .addCase(addCashAction.fulfilled, (state, action) => {
        state.addCashLoading = false;
        state.addCashResponse = action.payload;
        state.addCashError = null;
      })
      .addCase(addCashAction.rejected, (state, action) => {
        state.addCashLoading = false;
        state.addCashError = action.payload as string;
        state.addCashResponse = null;
      });

    builder
      .addCase(removeCashAction.pending, (state) => {
        state.removeCashLoading = true;
        state.removeCashError = null;
        state.removeCashResponse = null;
      })
      .addCase(removeCashAction.fulfilled, (state, action) => {
        state.removeCashLoading = false;
        state.removeCashResponse = action.payload;
        state.removeCashError = null;
      })
      .addCase(removeCashAction.rejected, (state, action) => {
        state.removeCashLoading = false;
        state.removeCashError = action.payload as string;
        state.removeCashResponse = null;
      });

    builder
      .addCase(closeStationCashierAction.pending, (state) => {
        state.closeStationCashierLoading = true;
        state.closeStationCashierError = null;
        state.closeStationCashierResponse = null;
      })
      .addCase(closeStationCashierAction.fulfilled, (state, action) => {
        state.closeStationCashierLoading = false;
        state.closeStationCashierResponse = action.payload;
        state.closeStationCashierError = null;
      })
      .addCase(closeStationCashierAction.rejected, (state, action) => {
        state.closeStationCashierLoading = false;
        state.closeStationCashierError = action.payload as string;
        state.closeStationCashierResponse = null;
      });

    builder
      .addCase(getAllStationSummaryAction.pending, (state) => {
        state.getAllStationSummaryLoading = true;
        state.getAllStationSummaryError = null;
        state.getAllStationSummaryResponse = null;
      })
      .addCase(getAllStationSummaryAction.fulfilled, (state, action) => {
        state.getAllStationSummaryLoading = false;
        state.getAllStationSummaryResponse = action.payload;
        state.getAllStationSummaryError = null;
      })
      .addCase(getAllStationSummaryAction.rejected, (state, action) => {
        state.getAllStationSummaryLoading = false;
        state.getAllStationSummaryError = action.payload as string;
        state.getAllStationSummaryResponse = null;
      });

    builder
      .addCase(getAllDayEndStationSummaryAction.pending, (state) => {
        state.dayEndStationSummaryLoading = true;
        state.dayEndStationSummaryError = null;
        state.dayEndStationSummaryResponse = null;
      })
      .addCase(getAllDayEndStationSummaryAction.fulfilled, (state, action) => {
        state.dayEndStationSummaryLoading = false;
        state.dayEndStationSummaryResponse = action.payload;
        state.dayEndStationSummaryError = null;
      })
      .addCase(getAllDayEndStationSummaryAction.rejected, (state, action) => {
        state.dayEndStationSummaryLoading = false;
        state.dayEndStationSummaryError = action.payload as string;
        state.dayEndStationSummaryResponse = null;
      });

    builder
      .addCase(endDayAction.pending, (state) => {
        state.endDayLoading = true;
        state.endDayError = null;
        state.endDayResponse = null;
      })
      .addCase(endDayAction.fulfilled, (state, action) => {
        state.endDayLoading = false;
        state.endDayResponse = action.payload;
        state.endDayError = null;
      })
      .addCase(endDayAction.rejected, (state, action) => {
        state.endDayLoading = false;
        state.endDayError = action.payload as string;
        state.endDayResponse = null;
      });

    builder
      .addCase(getCashierLatestSessionAction.pending, (state) => {
        state.cashierLatestSessionLoading = true;
        state.cashierLatestSessionError = null;
        state.cashierLatestSession = null;
      })
      .addCase(getCashierLatestSessionAction.fulfilled, (state, action) => {
        state.cashierLatestSessionLoading = false;
        state.cashierLatestSession = action.payload;
        state.cashierLatestSessionError = null;
      })
      .addCase(getCashierLatestSessionAction.rejected, (state, action) => {
        state.cashierLatestSessionLoading = false;
        state.cashierLatestSessionError = action.payload as string;
        state.cashierLatestSession = null;
      });
    builder
      .addCase(getAllCashierPointsActions.pending, (state) => {
        state.cashierPointsLoading = true;
        state.cashierPointsError = null;
        state.cashierPoints = null;
      })
      .addCase(getAllCashierPointsActions.fulfilled, (state, action) => {
        state.cashierPointsLoading = false;
        state.cashierPoints = action.payload;
        state.cashierPointsError = null;
      })
      .addCase(getAllCashierPointsActions.rejected, (state, action) => {
        state.cashierPointsLoading = false;
        state.cashierPointsError = action.payload as string;
        state.cashierPoints = null;
      });
    builder
      .addCase(getRefundsForStationAction.pending, (state) => {
        state.refundsForStationLoading = true;
        state.refundsForStationError = null;
        state.refundsForStation = null;
      })
      .addCase(getRefundsForStationAction.fulfilled, (state, action) => {
        state.refundsForStationLoading = false;
        state.refundsForStation = action.payload;
      })
      .addCase(getRefundsForStationAction.rejected, (state, action) => {
        state.refundsForStationLoading = false;
        state.refundsForStationError = action.payload as string;
        state.refundsForStation = null;
      });
    builder
      .addCase(getRefundsForStoreAction.pending, (state) => {
        state.refundsForStoreLoading = true;
        state.refundsForStoreError = null;
        state.refundsForStore = null;
      })
      .addCase(getRefundsForStoreAction.fulfilled, (state, action) => {
        state.refundsForStoreLoading = false;
        state.refundsForStore = action.payload;
      })
      .addCase(getRefundsForStoreAction.rejected, (state, action) => {
        state.refundsForStoreLoading = false;
        state.refundsForStoreError = action.payload as string;
        state.refundsForStore = null;
      });
    builder
      .addCase(getStoreCategorySummaryAction.pending, (state) => {
        state.getStoreCategorySummaryLoading = true;
        state.getStoreCategorySummaryError = null;
        state.getStoreCategorySummaryResponse = null;
      })
      .addCase(getStoreCategorySummaryAction.fulfilled, (state, action) => {
        state.getStoreCategorySummaryLoading = false;
        state.getStoreCategorySummaryResponse = action.payload;
        state.getStoreCategorySummaryError = null;
      })
      .addCase(getStoreCategorySummaryAction.rejected, (state, action) => {
        state.getStoreCategorySummaryLoading = false;
        state.getStoreCategorySummaryError = action.payload as string;
        state.getStoreCategorySummaryResponse = null;
      });

    builder
      .addCase(getCashierDetailsAction.pending, (state) => {
        state.cashierDetailsLoading = true;
        state.cashierDetailsError = null;
        state.cashierDetails = null;
      })
      .addCase(getCashierDetailsAction.fulfilled, (state, action) => {
        state.cashierDetailsLoading = false;
        state.cashierDetails = action.payload;
        state.cashierDetailsError = null;
      })
      .addCase(getCashierDetailsAction.rejected, (state, action) => {
        state.cashierDetailsLoading = false;
        state.cashierDetailsError = action.payload as string;
        state.cashierDetails = null;
      });
    builder
      .addCase(getStoreStatusAction.pending, (state) => {
        state.getStoreStatusLoading = true;
        state.getStoreStatusError = null;
        state.getStoreStatusResponse = null;
      })
      .addCase(getStoreStatusAction.fulfilled, (state, action) => {
        state.getStoreStatusLoading = false;
        state.getStoreStatusResponse = action.payload;
        state.getStoreStatusError = null;
      })
      .addCase(getStoreStatusAction.rejected, (state, action) => {
        state.getStoreStatusLoading = false;
        state.getStoreStatusError = action.payload as string;
        state.getStoreStatusResponse = null;
      })
      // Add new reducer cases for enhanced station management
      .addCase(getAllStationsWithStatusAction.pending, (state) => {
        state.getAllStationsWithStatusLoading = true;
        state.getAllStationsWithStatusError = null;
      })
      .addCase(getAllStationsWithStatusAction.fulfilled, (state, action) => {
        state.getAllStationsWithStatusLoading = false;
        state.allStationsWithStatus = action.payload;
      })
      .addCase(getAllStationsWithStatusAction.rejected, (state, action) => {
        state.getAllStationsWithStatusLoading = false;
        state.getAllStationsWithStatusError = action.payload as string;
      })
      .addCase(getAvailableStationsForOnboardingAction.pending, (state) => {
        state.getAvailableStationsForOnboardingLoading = true;
        state.getAvailableStationsForOnboardingError = null;
      })
      .addCase(
        getAvailableStationsForOnboardingAction.fulfilled,
        (state, action) => {
          state.getAvailableStationsForOnboardingLoading = false;
          state.availableStationsForOnboarding = action.payload;
        }
      )
      .addCase(
        getAvailableStationsForOnboardingAction.rejected,
        (state, action) => {
          state.getAvailableStationsForOnboardingLoading = false;
          state.getAvailableStationsForOnboardingError =
            action.payload as string;
        }
      )
      // Station activation/deactivation reducers
      .addCase(deactivateStationAction.pending, (state) => {
        // No loading state needed for individual station actions
      })
      .addCase(deactivateStationAction.fulfilled, (state, action) => {
        // Update the station in the allStationsWithStatus array
        if (state.allStationsWithStatus && action.payload) {
          const index = state.allStationsWithStatus.findIndex(
            (station) => station.id === action.payload.id
          );
          if (index !== -1) {
            state.allStationsWithStatus[index].isActive = false;
            state.allStationsWithStatus[index].occupationStatus = "Disabled";
          }
        }
      })
      .addCase(deactivateStationAction.rejected, (state, action) => {
        // Handle error if needed
      })
      .addCase(activateStationAction.pending, (state) => {
        // No loading state needed for individual station actions
      })
      .addCase(activateStationAction.fulfilled, (state, action) => {
        // Update the station in the allStationsWithStatus array
        if (state.allStationsWithStatus && action.payload) {
          const index = state.allStationsWithStatus.findIndex(
            (station) => station.id === action.payload.id
          );
          if (index !== -1) {
            state.allStationsWithStatus[index].isActive = true;
            // Determine occupation status based on device assignments
            // This is a simplified approach - in a real app, you might want to refetch the data
            state.allStationsWithStatus[index].occupationStatus = "Available";
          }
        }
      })
      .addCase(activateStationAction.rejected, (state, action) => {
        // Handle error if needed
      });

    // Station Access Sessions reducers
    builder
      .addCase(getStationAccessSessionsAction.pending, (state) => {
        state.stationAccessSessionsLoading = true;
        state.stationAccessSessionsError = null;
      })
      .addCase(getStationAccessSessionsAction.fulfilled, (state, action) => {
        state.stationAccessSessionsLoading = false;
        state.stationAccessSessions = action.payload;
        state.stationAccessSessionsError = null;
      })
      .addCase(getStationAccessSessionsAction.rejected, (state, action) => {
        state.stationAccessSessionsLoading = false;
        state.stationAccessSessionsError = action.payload as string;
        state.stationAccessSessions = null;
      });

    // Admin Close Station reducers
    builder
      .addCase(adminCloseStationAction.pending, (state) => {
        state.adminCloseStationLoading = true;
        state.adminCloseStationError = null;
      })
      .addCase(adminCloseStationAction.fulfilled, (state, action) => {
        state.adminCloseStationLoading = false;
        state.adminCloseStationResponse = action.payload;
        state.adminCloseStationError = null;
      })
      .addCase(adminCloseStationAction.rejected, (state, action) => {
        state.adminCloseStationLoading = false;
        state.adminCloseStationError = action.payload as string;
        state.adminCloseStationResponse = null;
      });
  },
});

export default cashierStationSlice.reducer;
export const {
  setActiveStation,
  setActiveStationCashier,
  setStationCashierHydrated,
  clearOpenStationCashierResponse,
  setIsRegisterCleaned,
  clearAddCashResponse,
  clearRemoveCashResponse,
  clearEndDayResponse,
} = cashierStationSlice.actions;
